import { theme as antdTheme } from "antd";
import { useTranslation } from "react-i18next";
import { AiOutlineCreditCard } from "react-icons/ai";

import { usePortalRoles } from "shell/platform/hooks/usePortalRoles";
import { useRoles } from "shell/platform/hooks/useRoles";

import type { MenuDeps, MenuItem } from "./types";

import { buildConsoleMenuItems } from "./helpers/buildConsoleMenu";
import { buildPortalMenuItems } from "./helpers/buildPortalMenu";
import { markMenuLabelsForReplay } from "./helpers/markMenuLabelsForReplay";

export const useMainMenu = (): MenuItem[] => {
  const { t } = useTranslation([
    "common",
    "clientAccess",
    "commonServices",
    "clientLifecycle",
  ]);
  const { hasRole, isLoading, hasNoRoles } = useRoles();
  const { hasPortalReadAccess, hasPanFinderAccess, hasWebhookAccess } =
    usePortalRoles();
  const { token } = antdTheme.useToken();

  const isInternal = hasRole(["Internal", "Internal Read Only"]);
  const isOrganisationManager = hasRole(["Organisation Manager"]);
  const isOrganisationAdmin = hasRole(["Organisation Admin"]);

  if (isLoading || hasNoRoles) return [];

  const deps: MenuDeps = {
    t,
    hasRole,
    isInternal,
    hasWebhookAccess,
    hasPanFinderAccess,
    colorPrimary: token?.colorPrimary,
  };

  const portalMenuItems = buildPortalMenuItems(deps);
  const consoleMenuItems = buildConsoleMenuItems(deps);

  let items: MenuItem[];

  if (isInternal && hasPortalReadAccess) {
    items = [
      {
        key: "thredd-portal",
        label: "Thredd Portal",
        "data-e2e": "threddPortalMenuItem",
        icon: <AiOutlineCreditCard color={token?.colorPrimary} />,
        children: portalMenuItems,
      },
      {
        type: "divider",
      },
      ...consoleMenuItems,
    ];
  } else if (isOrganisationManager) {
    items = hasPortalReadAccess
      ? [
          ...portalMenuItems,
          {
            type: "divider",
          },
          ...consoleMenuItems,
        ]
      : consoleMenuItems;
  } else if (isOrganisationAdmin && !hasPortalReadAccess) {
    items = portalMenuItems.filter(
      item => (item as { key?: string })?.key === "sys-admin",
    );
  } else if (hasPortalReadAccess) {
    items = portalMenuItems;
  } else {
    items = [];
  }

  return markMenuLabelsForReplay(items);
};
